import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

export interface ImageCaptionProps extends SharedProps {
  /**  Ex "Detta är en bildtext" */
  caption?: string
  /** Ex "Paul Hansen" */
  author?: string
  /** Type of image. Ex "Foto" or "Illustration" */
  imageType?: string
  /** Fixed pixel value is used for typography to prevent scaling based on html font-size */
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/image-caption](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/image-caption)
 * - Storybook: [ImageCaption](https://designsystem.dn.se/?path=/docs/article-image-caption--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/image-caption/image-caption.scss'`
 */
export const ImageCaption = ({ caption, author, imageType, classNames, attributes, forcePx }: ImageCaptionProps) => {
  const classes = formatClassString(['ds-image-caption', forcePx && 'ds-force-px', classNames])

  const authorText =
    imageType && author ? `${imageType.charAt(0).toUpperCase() + imageType.slice(1)}: ${author}` : author

  const authorClasses = [
    'ds-article-image__credits',
    authorText && authorText.length >= 27 && authorText.length < 60 // Low estimate of how much will fit on tablet/desktop
      ? 'ds-article-image__credits--nowrap-bigscreen'
      : authorText && authorText.length < 27 // Low estimate of how much will fit on 320px mobile
        ? 'ds-article-image__credits--nowrap'
        : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <figcaption className={classes} aria-hidden='true' {...attributes}>
      {/* This needs to be on the same line to maintain a space between the caption-span and author-span */}
      {caption && <span>{caption}</span>} {authorText && <span className={authorClasses}>{authorText}</span>}
    </figcaption>
  )
}
